import React, { useEffect, useState } from "react";
import { Bell, BellOff, BellRing, CheckCircle2, Loader2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
    pushSupported,
    notificationPermission,
    subscribeToPush,
    unsubscribeFromPush,
} from "@/lib/push";
import ConfirmModal from "./ConfirmModal";

/**
 * PushConnectCard — browser push notifications (VAPID).
 *
 * States:
 *   • not_supported — browser missing Push APIs (Safari < 16, etc.)
 *   • blocked       — Notification.permission === "denied"
 *   • not_subscribed — supported & permission default/granted, no active sub
 *   • subscribed    — at least one active sub for this user
 */
const PushConnectCard = ({ onChange }) => {
    const [status, setStatus] = useState({ loading: true });
    const [busy, setBusy] = useState(false);
    const [testing, setTesting] = useState(false);
    const [confirmDisable, setConfirmDisable] = useState(false);
    const [disabling, setDisabling] = useState(false);

    const fetchStatus = async () => {
        try {
            const r = await api.get("/push/status");
            setStatus({ ...r.data, loading: false });
            return r.data;
        } catch {
            setStatus({ subscribed: false, device_count: 0, loading: false });
            return null;
        }
    };

    useEffect(() => { fetchStatus(); }, []);

    const supported = pushSupported();
    const perm = supported ? notificationPermission() : "denied";

    const handleEnable = async () => {
        if (!supported) return;
        setBusy(true);
        try {
            await subscribeToPush();
            await fetchStatus();
            toast.success("Push-сповіщення увімкнено 🔔");
            onChange?.();
        } catch (e) {
            toast.error(e?.message || "Не вдалося увімкнути push");
        } finally {
            setBusy(false);
        }
    };

    const handleTest = async () => {
        setTesting(true);
        try {
            const r = await api.post("/push/test");
            if (r.data?.sent > 0) {
                toast.success(`Тест відправлено на ${r.data.sent} пристрій(в)`);
            } else if (r.data?.expired > 0) {
                toast.error("Підписка застаріла — увімкніть push ще раз");
                await fetchStatus();
            } else {
                toast.error("Не вдалося відправити тест");
            }
        } catch (e) {
            toast.error("Помилка надсилання тесту");
        } finally {
            setTesting(false);
        }
    };

    const handleDisable = async () => {
        setDisabling(true);
        try {
            await unsubscribeFromPush();
            await fetchStatus();
            toast.success("Push-сповіщення вимкнено");
            setConfirmDisable(false);
            onChange?.();
        } catch {
            toast.error("Не вдалося вимкнути");
        } finally {
            setDisabling(false);
        }
    };

    // ── Loading ──────────────────────────────────────────────────
    if (status.loading) {
        return (
            <div className="flex items-center gap-2 text-sm text-faint-c py-3"
                 data-testid="push-card-loading">
                <Loader2 size={14} className="animate-spin" />
                Перевіряю стан push…
            </div>
        );
    }

    // ── NOT SUPPORTED ────────────────────────────────────────────
    if (!supported) {
        return (
            <div className="rounded-xl border border-border bg-surface-hover/10 p-4"
                 data-testid="push-card-not-supported">
                <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-faint/10 flex items-center justify-center shrink-0">
                        <BellOff size={18} className="text-faint-c" />
                    </div>
                    <div className="flex-1">
                        <div className="font-display font-semibold text-text-primary">
                            Push-сповіщення недоступні
                        </div>
                        <p className="mt-1 text-xs text-secondary-c leading-relaxed">
                            Ваш браузер не підтримує web push. Скористайтеся{" "}
                            <span className="font-mono">Chrome / Firefox / Edge</span> на компʼютері
                            або Android.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    // ── BLOCKED ──────────────────────────────────────────────────
    if (perm === "denied") {
        return (
            <div className="rounded-xl border border-warning/40 bg-warning/5 p-4"
                 data-testid="push-card-blocked">
                <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-warning/15 flex items-center justify-center shrink-0">
                        <AlertCircle size={18} className="text-warning-c" />
                    </div>
                    <div className="flex-1">
                        <div className="font-display font-semibold text-text-primary">
                            Сповіщення заблоковано
                        </div>
                        <p className="mt-1 text-xs text-secondary-c leading-relaxed">
                            Ви раніше відмовили AutoAds у дозволі на сповіщення. Щоб увімкнути —
                            натисніть <span className="font-mono">🔒</span> зліва від адреси сайту у
                            браузері → Дозволи → Сповіщення → <strong>Дозволити</strong>, потім
                            оновіть сторінку.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    // ── SUBSCRIBED ───────────────────────────────────────────────
    if (status.subscribed) {
        return (
            <div className="rounded-xl border border-success/40 bg-success/5 p-4"
                 data-testid="push-card-subscribed">
                <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-success/15 flex items-center justify-center shrink-0">
                        <BellRing size={18} className="text-success-c" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                            <span className="font-display font-semibold text-text-primary">
                                Push увімкнено
                            </span>
                            <CheckCircle2 size={14} className="text-success-c" />
                        </div>
                        <div className="mt-1 text-xs text-secondary-c">
                            Активно на {status.device_count} пристрої{status.device_count === 1 ? "" : "(ях)"}
                        </div>
                    </div>
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                    <button
                        type="button"
                        onClick={handleTest}
                        disabled={testing}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md
                                   bg-surface-hover/40 hover:bg-surface-hover/60 border border-border
                                   text-text-primary text-xs font-semibold transition-colors
                                   disabled:opacity-60 disabled:cursor-wait"
                        data-testid="push-test"
                    >
                        {testing
                            ? <Loader2 size={12} className="animate-spin" />
                            : <Bell size={12} />}
                        <span>Надіслати тест</span>
                    </button>
                    <button
                        type="button"
                        onClick={() => setConfirmDisable(true)}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md
                                   border border-error/30 text-error-c
                                   hover:bg-error/10 text-xs font-semibold transition-colors"
                        data-testid="push-disable"
                    >
                        <BellOff size={12} />
                        <span>Вимкнути</span>
                    </button>
                </div>

                <ConfirmModal
                    open={confirmDisable}
                    onClose={() => setConfirmDisable(false)}
                    onConfirm={handleDisable}
                    loading={disabling}
                    danger
                    title="Вимкнути push?"
                    body="Браузер більше не показуватиме сповіщення AutoAds. Інші канали (in-app, email, Telegram) залишаться як налаштовано."
                    confirmLabel="Так, вимкнути"
                    cancelLabel="Залишити"
                    testidPrefix="push-disable-modal"
                />
            </div>
        );
    }

    // ── NOT SUBSCRIBED (default) ─────────────────────────────────
    return (
        <div className="rounded-xl border border-border bg-surface-hover/10 p-4"
             data-testid="push-card-not-subscribed">
            <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    <Bell size={18} className="text-primary-c" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="font-display font-semibold text-text-primary">
                        Push-сповіщення у браузері
                    </div>
                    <p className="mt-1 text-xs text-secondary-c leading-relaxed">
                        Отримуйте критичні алерти й готові звіти прямо в системних сповіщеннях —
                        навіть коли вкладка AutoAds закрита. Працює в Chrome, Firefox, Edge.
                    </p>
                </div>
            </div>

            <button
                type="button"
                onClick={handleEnable}
                disabled={busy}
                className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-md
                           bg-primary hover:bg-primary/90 text-white text-xs font-semibold transition-colors
                           disabled:opacity-60 disabled:cursor-wait"
                data-testid="push-enable"
            >
                {busy ? <Loader2 size={12} className="animate-spin" /> : <Bell size={12} />}
                <span>Увімкнути push</span>
            </button>
        </div>
    );
};

export default PushConnectCard;
